import { HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '@env';
import { MESSAGE } from '@shared/constants';
import { IS_EXTENDED_ERRORS, SKIP_ERROR_NOTIFICATION } from '@shared/interceptors';
import { AuthTokenResponse, ChangePasswordProps, ExpiredPasswordProps, LoginTenantResponse } from '@shared/models';
import { APP_ENV_CONFIG } from '@shared/services';
import { BackendService } from '@shared/services/backend.service';
import { getRealmName } from '@shared/utils';

interface FirebaseRequest {
  email: string;
  password: string;
  returnSecureToken: boolean;
  tenantId: string | null;
}

interface FirebaseResponse {
  status: number;
  error: unknown;
}

interface FirebaseInterceptionData {
  request?: FirebaseRequest;
  response?: FirebaseResponse;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  envConfig = inject(APP_ENV_CONFIG);

  afAuth?: Auth;

  get realmName(): string {
    return getRealmName();
  }

  constructor(private backendService: BackendService) {}

  // Login - Step 0
  public getAuthTenantInfo(tenant = this.realmName): Observable<LoginTenantResponse> {
    return this.backendService.get<LoginTenantResponse>(`${environment.uaaService}/${tenant}/tenant-info`, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  // Maintenance API
  public getMaintenanceInfo(): Observable<LoginTenantResponse> {
    return this.backendService.get<LoginTenantResponse>('/status', { responseType: 'text' });
  }

  /**
   * Initializes (or reuses) the Firebase app for the given tenant and returns its Auth instance.
   * Reusing the existing default app avoids re-initializing on subsequent re-authentications.
   */
  private getTenantAuth(tenantId?: string): Auth {
    const app = getApps().length ? getApp() : initializeApp(this.envConfig.firebase);
    const auth = getAuth(app);
    auth.tenantId = tenantId || null;
    return auth;
  }

  public googleCloudLogin(email: string, password: string, tenantId?: string): Observable<string> {
    this.afAuth = this.getTenantAuth(tenantId);

    const requestPayload = {
      email,
      password: '[REDACTED]',
      returnSecureToken: true,
      tenantId: tenantId || null,
    };

    const capturedData = this.setupFirebaseInterception();

    return from(signInWithEmailAndPassword(this.afAuth, email, password)).pipe(
      catchError((firebaseError) => {
        const actualHttpStatus = capturedData.response?.status || this.mapFirebaseErrorToHttpStatus(firebaseError.code);
        const actualResponse = capturedData.response?.error || {
          error: {
            code: firebaseError.code,
            message: firebaseError.message,
          },
        };

        const failureData = {
          userLoginName: email,
          tenantSlug: this.realmName,
          authProvider: 'GCP_IDENTITY_PLATFORM',
          attemptedToAuthenticateAt: new Date(),
          url: `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${this.envConfig.firebase.apiKey}`,
          httpMethod: 'POST',
          responseHttpCode: actualHttpStatus,
          identityProviderRequest: JSON.stringify(capturedData.request || requestPayload),
          identityProviderResponse: JSON.stringify(actualResponse),
        };

        this.sendLoginFailureToUAA(failureData).subscribe({
          error: (uaaError) => console.error('Failed to log authentication failure to UAA:', uaaError),
        });

        // A disabled account surfaces as `auth/user-disabled` from the SDK and `USER_DISABLED` in the raw provider response.
        const providerError = capturedData.response?.error as { error?: { message?: string } } | undefined;
        const isUserDisabled = firebaseError.code === 'auth/user-disabled' || providerError?.error?.message === 'USER_DISABLED';

        return throwError(() => new Error(isUserDisabled ? MESSAGE.ACCOUNT_LOCKED_ERROR : MESSAGE.LOGIN_ERROR));
      }),
      switchMap(() => {
        if (this.afAuth?.currentUser) {
          return from(this.afAuth.currentUser.getIdToken());
        } else {
          return throwError(() => new Error(MESSAGE.UNKNOWN_ERROR));
        }
      }),
    );
  }

  private mapFirebaseErrorToHttpStatus(firebaseErrorCode: string): number {
    const errorMap: Record<string, number> = {
      'auth/invalid-email': 400,
      'auth/user-disabled': 403,
      'auth/user-not-found': 400,
      'auth/wrong-password': 400,
      'auth/invalid-password': 400,
      'auth/too-many-requests': 429,
      'auth/network-request-failed': 500,
      'auth/internal-error': 500,
    };

    return errorMap[firebaseErrorCode] || 400;
  }

  private setupFirebaseInterception(): FirebaseInterceptionData {
    const capturedData: FirebaseInterceptionData = {};

    if ((window as unknown as { _firebaseInterceptorPatched?: boolean })._firebaseInterceptorPatched) {
      return capturedData;
    }

    const originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

      if (url.includes('identitytoolkit.googleapis.com/v1/accounts:signInWithPassword')) {
        try {
          const requestBody = init?.body ? JSON.parse(init.body as string) : {};
          capturedData.request = {
            ...requestBody,
            password: '[REDACTED]',
          };

          const response = await originalFetch(input, init);
          const responseClone = response.clone();

          if (!response.ok) {
            const errorBody = await responseClone.json();
            capturedData.response = {
              status: response.status,
              error: errorBody,
            };
          }

          return response;
        } catch {
          return originalFetch(input, init);
        }
      }

      return originalFetch(input, init);
    };

    (window as unknown as { _firebaseInterceptorPatched?: boolean })._firebaseInterceptorPatched = true;
    return capturedData;
  }

  private sendLoginFailureToUAA(failureData: {
    userLoginName: string;
    tenantSlug: string;
    authProvider: string;
    attemptedToAuthenticateAt: Date;
    url: string;
    httpMethod: string;
    responseHttpCode: number;
    identityProviderRequest: string;
    identityProviderResponse: string;
  }): Observable<unknown> {
    return this.backendService
      .post(`${environment.uaaService}/track/login`, {
        userLoginName: failureData.userLoginName,
        tenantSlug: failureData.tenantSlug,
        authProvider: failureData.authProvider,
        attemptedToAuthenticateAt: failureData.attemptedToAuthenticateAt,
        url: failureData.url,
        httpMethod: failureData.httpMethod,
        responseHttpCode: failureData.responseHttpCode,
        identityProviderRequest: failureData.identityProviderRequest,
        identityProviderResponse: failureData.identityProviderResponse,
      })
      .pipe(
        catchError((error) => {
          console.error('UAA tracking service error:', error);
          return of(null);
        }),
      );
  }

  /**
   * Login - Step 2 - FIREBASE | GCP_IDENTITY_PLATFORM
   * API {{base-url}}/uaa/api/auth/{{tenant}}/exchange-token
   *
   * @param accessToken id token from auth provider FIREBASE | GCP_IDENTITY_PLATFORM
   * @param [tenant] realm name from user input
   * @returns {Observable<AuthTokenResponse>}
   */
  public exchangeToken(accessToken: string, tenant = this.realmName): Observable<AuthTokenResponse> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);

    return this.backendService.post<AuthTokenResponse>(
      `${environment.uaaService}/${tenant}/exchange-token`,
      { externalToken: accessToken },
      { headers },
    );
  }

  // Refresh token
  public refreshToken(businessAccountId: string, tenant = this.realmName): Observable<AuthTokenResponse> {
    return this.backendService.post(`${environment.uaaService}/${tenant}/business-accounts/${businessAccountId}/token`);
  }

  // Forgot Password
  public forgotPassword(email: string, tenant = this.realmName): Observable<boolean> {
    return this.backendService
      .post(`${environment.accountFlowEndpoint}/api/auth/realms/${tenant}/forgot-password`, { email })
      .pipe(map(() => true));
  }

  // Logout
  public signOut(tenant = this.realmName): Observable<boolean> {
    // Without a tenant there is no server session to end, and the call would 404 on `/auth//logout`
    if (!tenant) {
      void this.afAuth?.signOut();

      return of(true);
    }

    return this.backendService.post(`${environment.uaaService}/${tenant}/logout`).pipe(
      tap({ next: () => this.afAuth?.signOut() }),
      map(() => true),
      catchError(() => of(true)),
    );
  }

  /**
   * Re-authenticates the current user against the auth provider using their current password and
   * returns a fresh provider (Firebase ID) token. A genuine sign-in is required — not a token
   * refresh — so the token carries a fresh `auth_time` claim, which the backend validates as proof
   * of recent authentication before allowing a password change (PCI remediation).
   */
  public getReauthProviderToken(email: string, currentPassword: string): Observable<string> {
    return this.getAuthTenantInfo().pipe(
      switchMap(({ providerTenantId }) => {
        this.afAuth = this.getTenantAuth(providerTenantId);
        return from(signInWithEmailAndPassword(this.afAuth, email, currentPassword));
      }),
      switchMap((credential) => from(credential.user.getIdToken())),
      catchError((error) => {
        const errorCode = (error as { code?: string })?.code;
        const isWrongPassword = errorCode === 'auth/wrong-password' || errorCode === 'auth/invalid-credential';
        return throwError(() => new Error(isWrongPassword ? MESSAGE.CURRENT_PASSWORD_INCORRECT : MESSAGE.UNKNOWN_ERROR));
      }),
    );
  }

  changePassword(changePasswordData: ChangePasswordProps): Observable<void> {
    return this.backendService.update<void>(
      `${environment.uaaPortalService}/tenants/${this.realmName}/change-password`,
      changePasswordData,
      { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) },
    );
  }

  /**
   * PCI recovery flow: sets a new password after login was blocked by expiry.
   * Unauthenticated (no session cookies) — authorized solely by the fresh provider
   * token from the sign-in just performed. Skips the global error notification so the
   * caller can surface field-level (400) and stale-token (403) errors on the screen.
   */
  expiredPassword(expiredPasswordData: ExpiredPasswordProps, tenant = this.realmName): Observable<void> {
    return this.backendService.post<void>(`${environment.uaaService}/${tenant}/expired-password`, expiredPasswordData, {
      context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true),
    });
  }
}
